import { useState } from 'react';
import { auditApi } from '../../api/services';
import type { AuditQuery } from '../../api/types';
import {
  Badge, Button, Field, Input, Loadable, PageHeader, Pager, Select, Table, fmtDateTime, todayIso, useAction, useAsync,
} from '../../ui/components';
import { PageHelp } from '../../ui/help';

const daysAgoIso = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Bitácora: quién hizo cada operación, cuándo y con qué resultado. Sólo consulta. */
export function AuditPage() {
  const [filters, setFilters] = useState<AuditQuery>({ dateFrom: daysAgoIso(7), dateTo: todayIso(), page: 1, pageSize: 50 });
  const [draft, setDraft] = useState<AuditQuery>(filters);
  const modules = useAsync(() => auditApi.modules(), []);
  const rows = useAsync(() => auditApi.search(filters), [JSON.stringify(filters)]);
  const { run, busy } = useAction();
  const apply = () => setFilters({ ...draft, page: 1 });

  return (
    <>
      <PageHeader title="Bitácora" subtitle="Registro de las operaciones que cambian datos en la contabilidad"
        actions={<Button busy={busy} onClick={() => run(() => auditApi.exportCsv(filters))}>Exportar a Excel (CSV)</Button>} />
      <PageHelp page="audit" />
      <div className="acc-toolbar">
        <Field label="Desde"><Input type="date" value={draft.dateFrom ?? ''} onChange={(v) => setDraft({ ...draft, dateFrom: v })} /></Field>
        <Field label="Hasta"><Input type="date" value={draft.dateTo ?? ''} onChange={(v) => setDraft({ ...draft, dateTo: v })} /></Field>
        <Field label="Pantalla">
          <Select value={draft.module ?? ''} onChange={(v) => setDraft({ ...draft, module: v || undefined })} placeholder="Todas"
            options={(modules.data ?? []).map((m) => ({ value: m, label: m }))} />
        </Field>
        <Field label="Origen">
          <Select value={draft.origin ?? ''} onChange={(v) => setDraft({ ...draft, origin: v || undefined })} placeholder="Todos"
            options={[{ value: 'PANTALLA', label: 'Pantallas' }, { value: 'INTEGRACION', label: 'Integración (facturación y pagos)' }]} />
        </Field>
        <Field label="Resultado">
          <Select value={draft.result ?? ''} onChange={(v) => setDraft({ ...draft, result: v || undefined })} placeholder="Todos"
            options={[{ value: 'CORRECTO', label: 'Correcto' }, { value: 'RECHAZADO', label: 'Rechazado' }, { value: 'ERROR', label: 'Error' }]} />
        </Field>
        <Field label="Buscar" hint="Usuario, acción, referencia o detalle"><Input value={draft.search ?? ''} onChange={(v) => setDraft({ ...draft, search: v || undefined })} maxLength={100} /></Field>
        <Button kind="primary" onClick={apply}>Buscar</Button>
      </div>
      <Loadable state={rows}>
        {(r) => (
          <>
            <Table rows={r.items} rowKey={(x) => x.id} empty="No hay registros con esos filtros." columns={[
              { key: 'f', title: 'Fecha y hora', render: (x) => fmtDateTime(x.date) },
              { key: 'u', title: 'Usuario', render: (x) => x.userName },
              { key: 'o', title: 'Origen', render: (x) => (x.origin === 'INTEGRACION' ? 'Integración' : 'Pantalla') },
              { key: 'm', title: 'Pantalla', render: (x) => x.module },
              { key: 'a', title: 'Acción', render: (x) => x.action },
              { key: 'r', title: 'Referencia', render: (x) => x.reference ?? '' },
              { key: 's', title: 'Resultado', render: (x) => <Badge tone={x.result === 'CORRECTO' ? 'ok' : x.result === 'RECHAZADO' ? 'warn' : 'bad'}>{x.result === 'CORRECTO' ? 'Correcto' : x.result === 'RECHAZADO' ? 'Rechazado' : 'Error'}</Badge> },
              { key: 'd', title: 'Detalle', render: (x) => x.detail ?? '' },
            ]} />
            <Pager page={r.page} totalPages={r.totalPages} total={r.total} onPage={(p) => setFilters({ ...filters, page: p })} />
          </>
        )}
      </Loadable>
    </>
  );
}
